import { defineComponent as _defineComponent } from 'vue'
import type { HoleType, PartType, SlotType } from '@/partTypes'
import store from '@/store'
import { calculateSlantedRectanglePath } from '@/util/commonFun'
import { LineSlotType } from '@/util/dealPlankDataFuncs'
import Scene from '@/util/dealPlankExtraDraw'
import {
  dealCurveHoles,
  getPathsMaxAreaObj,
  getThroughPathObj,
} from '@/util/drawPlankFuncs'
import { getSlotBasicInfo } from '@/util/plankCommonFuncs'
import { nextTick, onMounted, reactive, ref, watch } from 'vue'

import { tips } from './data'


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    part: null,
    isExistsActivePlank: { type: Boolean },
    conflictIndex: null
  },
  emits: ['searchConflictingPlates', 'closeConflictDiagram'],
  setup(__props: any, { emit: emits }) {

const props = __props as {
  part: any
  isExistsActivePlank?: boolean
  conflictIndex: number
}

const fillStyle = 'rgba(238, 238, 161)'
/* eslint-disable */



// 记录冲突视图的位置
const styleState = reactive({
  left: '100px',
  top: '50%',
})
const canvasState = reactive({
  width: 400,
  height: 400,
})
//记录当前冲突小板序号
const recordSerialNumber = ref(0)

const scale = ref(1) // 缩放倍数
const zoomPercent = ref(100) //缩放百分比
const clipPath = ref<{ x: number; y: number }[]>([])
// 绘制的起点
const startX = 50,
  startY = 100

// 绘制冲突框时设置边框和图形之间的间距
const gap = 2
//记录存在孔槽冲突的板件名称数组
const plankNameList = ref<any[]>([])
//记录存在孔槽冲突的板件数组
const canvasRef = ref<HTMLCanvasElement>()
const ctx = ref<CanvasRenderingContext2D | null>()
const part = ref<PartType>() // 当前板件
const isShow = ref(true)
const isShowCanvas = ref(true)
const scene = ref<InstanceType<typeof Scene>>()
// 冲突数量
const conflictNum = ref<boolean | number>(0)
// 冲突图离起始点的距离
const startXBase = ref(0)
const startYBase = ref(0)
// 是否点击了框出冲突部分
const isClickConflict = ref(false)

//关闭孔槽冲突图
function handleCloseConflictDiagram() {
  emits('closeConflictDiagram')
}
// 计算冲突数量 并判断是否显示冲突图
function calcConflictNum() {
  const count = {
    beyond: 0,
    intersect: 0,
  }
  const str = new Set()
  const conflicts = [
    ...(part.value?.holes ?? []),
    ...(part.value?.slots ?? []),
    ...(part.value?.lineSlots ?? []),
  ]
  for (const it of conflicts) {
    if (!it._conflict_uniqueId) return false
    it.conflict?.forEach((i) => {
      if (i.conflictType === 'beyond') {
        count.beyond += 1
      } else {
        const id = it._conflict_uniqueId
        const cid = i.conflictObj._conflict_uniqueId
        const strId = [id, cid].sort().join('')
        str.add(strId)
        count.intersect = str.size
      }
    })
  }
  const num = count.beyond + count.intersect
  return num < 0 ? 0 : num
}
// 缩放
function calcScale(num: number) {
  return num * scale.value
}
// 更新冲突的孔槽 由于页面可对板件进行操作
function updateConflict(part: PartType) {
  const holeSlotList = [...part.holes, ...part.slots, ...(part.lineSlots ?? [])]
  holeSlotList.forEach((it) => {
    it.conflict?.forEach((conflict) => {
      const target = conflict.conflictObj
      if (!target) return
      for (const it of holeSlotList as any) {
        if (target._conflict_uniqueId === it._conflict_uniqueId) {
          const obj = JSON.parse(JSON.stringify(it))
          Reflect.deleteProperty(obj, 'conflict')
          conflict.conflictObj = obj
          break
        }
      }
    })
  })
}

// 点击放大和缩小
function handleZoom(num: number) {
  num > 0 ? scene.value?.zoomIn() : scene.value?.zoomOut()
  zoomPercent.value = +zoomPercent.value + num
  if (zoomPercent.value <= 50) {
    zoomPercent.value = 50
  }
}
// 渲染
function startRender() {
  ctx.value?.clearRect(0, 0, 1000, 1000)
  ctx.value!.strokeStyle = '#000'
  updateConflict(part.value!)
  conflictNum.value = calcConflictNum()
  if (conflictNum.value !== 0 && !conflictNum.value) {
    isShowCanvas.value = false
    return
  } else {
    isShowCanvas.value = true
  }
  const { rect } = part.value!
  const [baseWidth, baseHeight] = [
    canvasState.width - 20,
    canvasState.height - 20,
  ]
  ;(startXBase.value = startX), (startYBase.value = startY)
  // 宽高比
  scale.value = +Math.min(
    baseWidth / rect.width,
    baseHeight / rect.height
  ).toFixed(1)
  // 板件绘制宽高
  const drawWidth = calcScale(rect.width)
  const drawHeight = calcScale(rect.height)
  // 居中绘制
  if (drawWidth < baseWidth) {
    const w = (baseWidth - drawWidth) / 2
    startXBase.value += w
  }
  if (drawHeight < baseHeight) {
    const h = (baseHeight - drawHeight) / 2
    startYBase.value += h
  }
  drawShape(startXBase.value, startYBase.value, part.value!)
  // 创建裁剪路径
  ctx.value?.save()
  clipPath.value.forEach((it, idx) => {
    if (idx) {
      ctx.value?.lineTo(it.x, it.y)
    } else {
      ctx.value?.moveTo(it.x, it.y)
    }
  })
  // 裁剪
  ctx.value?.clip()
  drawHoles(startXBase.value, startYBase.value, part.value!, true)
  drawSlots(startXBase.value, startYBase.value, part.value!, true)
  // 重新渲染一次孔槽的边框，由于冲突情况下边框会出现问题
  drawHoles(startXBase.value, startYBase.value, part.value!)
  drawSlots(startXBase.value, startYBase.value, part.value!)
  // 恢复路径
  ctx.value?.restore()
  // 绘制超出的冲突那一部分
  drawBeyondConflict(startXBase.value, startYBase.value, part.value!)
  clipPath.value = []
  alreadyDraw.clear()
}
// 绘制小板轮廓
function drawShape(startX: number, startY: number, part: PartType) {
  ctx.value!.fillStyle = 'transparent'
  const [width, height] = [
    calcScale(part.rect.width),
    calcScale(part.rect.height),
  ]
  if (!part.path) {
    ctx.value?.fillRect(startX, startY, width, height)
    ctx.value?.strokeRect(startX, startY, width, height)
    clipPath.value = [
      { x: startX, y: startY },
      { x: startX + width, y: startY },
      { x: startX + width, y: startY + height },
      { x: startX, y: startY + height },
    ]
  } else {
    // 记录板件轮廓的点位顺逆时针
    let area = window.ClipperLib.JS.AreaOfPolygon(part.path[0])
    let firstDirection = area > 0 ? false : true
    ctx.value?.beginPath()
    // 打穿长圆孔的加入异形的绘制
    const finalCurveHoles = dealCurveHoles(part.curveHoles)
    const throughCurveHoles = getThroughPathObj(finalCurveHoles, part.thick)
    // 获取面积最大的牛角槽
    const maxMillInfo = getPathsMaxAreaObj(part.millInfo, 'shape')
    const throughMillInfo = getThroughPathObj(maxMillInfo, part.thick)
    let newArr = [
      ...part.path,
      ...throughCurveHoles.map((it: any) => it.path).filter((it: any) => it),
      ...throughMillInfo.map((it: any) => it.shape),
    ]
    newArr.forEach((shape, idx) => {
      if (idx > 0) {
        let area2 = window.ClipperLib.JS.AreaOfPolygon(shape)
        let direction = area2 < 0
        if (direction === firstDirection) {
          shape.reverse()
        }
      }
      shape.forEach((point: { x: number; y: number }, idx: number) => {
        if (!idx) {
          ctx.value?.moveTo(
            startX + calcScale(point.x),
            startY + calcScale(point.y)
          )
        } else {
          ctx.value?.lineTo(
            startX + calcScale(point.x),
            startY + calcScale(point.y)
          )
        }
      })
      if (!idx) {
        clipPath.value = shape.map((point: { x: number; y: number }) => ({
          x: startX + calcScale(point.x),
          y: startY + calcScale(point.y),
        }))
      }
    })
    ctx.value?.stroke()
    ctx.value?.fill()
  }
}
// 绘制孔
function drawHoles(
  startX: number,
  startY: number,
  part: PartType,
  flag = false
) {
  // 绘制未冲突孔槽
  const { holes } = part
  const conflictHoles: PartType['holes'] = []
  for (const hole of holes) {
    if (hole.conflict && flag && hole.conflict[0].conflictType !== 'beyond') {
      conflictHoles.push(hole)
      continue
    }
    drawOneHole(startX, startY, hole)
  }
  if (flag) {
    conflictHoles.forEach((holes) => {
      drawConflictIntersectShape(startX, startY, holes)
    })
  }
}
// 绘制单个孔
function drawOneHole(
  startX: number,
  startY: number,
  hole: PartType['holes'][0],
  fillStyle?: string
) {
  ctx.value?.beginPath()
  if (hole.side == 1) {
    ctx.value!.strokeStyle = '#f008'
  } else {
    ctx.value!.strokeStyle = '#00f8'
  }
  ctx.value?.arc(
    startX + calcScale(hole.center.x),
    startY + calcScale(hole.center.y),
    calcScale(hole.diameter / 2),
    0,
    Math.PI * 2
  )
  if (fillStyle) {
    ctx.value!.fillStyle = fillStyle
    ctx.value?.fill()
  }
  ctx.value?.stroke()
  ctx.value?.closePath()
}
// 绘制槽
function drawSlots(
  startX: number,
  startY: number,
  part: PartType,
  flag = false
) {
  // 绘制未冲突孔槽
  const { slots } = part
  const conflictSlots: (SlotType | LineSlotType)[] = []
  const allSlots = [...slots, ...(part.lineSlots ?? [])]
  for (const slot of allSlots) {
    if (
      slot.conflict &&
      flag &&
      slot.conflict.some((it) => it.conflictType !== 'beyond')
    ) {
      conflictSlots.push(slot)
      continue
    }
    drawOneSlot(startX, startY, slot)
  }
  if (flag && conflictSlots.length) {
    conflictSlots.forEach((slots) => {
      drawConflictIntersectShape(startX, startY, slots)
    })
  }
}
// 绘制斜槽
function drawObliqueSlot(
  startX: number,
  startY: number,
  slot: PartType['slots'][0]
) {
  ctx.value?.beginPath()
  const obliqueSlot: any = calculateSlantedRectanglePath(
    slot.pt1,
    slot.pt2,
    slot.width
  )
  obliqueSlot.path.forEach((point: { x: number; y: number }) => {
    ctx.value?.lineTo(startX + calcScale(point.x), startY + calcScale(point.y))
  })
  ctx.value?.stroke()
}
// 绘制单个槽
function drawOneSlot(
  startX: number,
  startY: number,
  slot: SlotType | LineSlotType,
  fillStyle?: string
) {
  ctx.value?.beginPath()
  if (slot.side == 1) {
    ctx.value!.strokeStyle = '#f008'
  } else {
    ctx.value!.strokeStyle = '#00f8'
  }
  if (slot.symbol === 'lineSlot') {
    const { polygonArr } = slot as LineSlotType
    polygonArr.forEach((polygon) => {
      polygon.forEach((v, i) => {
        if (!i) {
          ctx.value?.moveTo(startX + calcScale(v.x), startY + calcScale(v.y))
        } else {
          ctx.value?.lineTo(startX + calcScale(v.x), startY + calcScale(v.y))
        }
      })
      ctx.value?.stroke()
      ctx.value?.closePath()
    })
    if (fillStyle) {
      ctx.value!.fillStyle = fillStyle
      ctx.value?.fill()
    }
  } else {
    const { x, y, width, height } = getSlotBasicInfo(slot, 'pt')
    if (fillStyle) {
      ctx.value!.fillStyle = fillStyle
      // 斜槽
      if (slot.symbol === 'CCompSlot') {
        drawObliqueSlot(startX, startY, slot as SlotType)
      } else {
        ctx.value?.fillRect(
          startX + calcScale(x),
          startY + calcScale(y),
          calcScale(width),
          calcScale(height)
        )
      }
    }
    // 斜槽
    if (slot.symbol === 'CCompSlot') {
      drawObliqueSlot(startX, startY, slot as SlotType)
    } else {
      ctx.value?.strokeRect(
        startX + calcScale(x),
        startY + calcScale(y),
        calcScale(width),
        calcScale(height)
      )
    }
    ctx.value?.closePath()
  }
}
// 记录已绘制的孔槽数据，冲突数据中保存着其他冲突的孔槽，已绘制后不需要再次绘制
const alreadyDraw = new Set<string>()
// 绘制单个冲突
function drawOneConflictIntersectShape(
  startX: number,
  startY: number,
  shape: any,
  color?: string
) {
  if (shape.pt1 || shape.symbol === 'lineSlot') {
    drawOneSlot(startX, startY, shape, color)
  } else {
    drawOneHole(startX, startY, shape, color)
  }
}
//点击下一个切换冲突小板
function handleNextBtn() {
  recordSerialNumber.value++
  if (recordSerialNumber.value === plankNameList.value.length) {
    recordSerialNumber.value = 0
  }
  emits(
    'searchConflictingPlates',
    plankNameList.value[recordSerialNumber.value],
    recordSerialNumber.value
  )
}
// 监听滚轮滚动，修改缩放比例
function handleWheelScale(e: any) {
  zoomPercent.value = (scene.value.getScaleRate() * 100).toFixed(0)
}
function handleLastBtn() {
  recordSerialNumber.value--
  if (recordSerialNumber.value < 0) {
    recordSerialNumber.value = plankNameList.value.length - 1
  }
  emits(
    'searchConflictingPlates',
    plankNameList.value[recordSerialNumber.value],
    recordSerialNumber.value
  )
}
// 绘制冲突
function drawConflictIntersectShape(
  startX: number,
  startY: number,
  shape: any
) {
  const key = shape._conflict_uniqueId
  if (alreadyDraw.has(key) && shape.type === 'slot') {
    alreadyDraw.add(key)
    drawOneConflictIntersectShape(startX, startY, shape, fillStyle)
  }
  const conflicts = shape.conflict
  for (const current of conflicts) {
    const key = current.conflictObj._conflict_uniqueId
    if (key && !alreadyDraw.has(key)) {
      // 第一次填充保留源图像上冲突部分
      ctx.value!.globalCompositeOperation = 'source-atop'
      drawOneConflictIntersectShape(
        startX,
        startY,
        current.conflictObj,
        '#e5ae67'
      )
      // 再绘制一次源图像但是目标图像在源图像之上
      ctx.value!.globalCompositeOperation = 'destination-over'
      drawOneConflictIntersectShape(
        startX,
        startY,
        current.conflictObj,
        fillStyle
      )
      alreadyDraw.add(key)
    }
    // 复原 不采用save的方式
    ctx.value!.globalCompositeOperation = 'source-over'
  }
}
// 绘制超出板件的冲突孔槽
function drawBeyondConflict(startX: number, startY: number, part: PartType) {
  // 没有好的方案了  ╮(╯▽╰)╭)
  const beyondList = [...part.holes, ...part.slots].filter((it) =>
    it.conflict?.find((item) => item.conflictType === 'beyond')
  ) as any
  if (!beyondList.length) return
  ctx.value!.globalCompositeOperation = 'destination-over'
  beyondList.forEach((it: any) => {
    // 孔槽分开绘制
    if (it.pt1 || it.symbol === 'lineSlot') {
      drawOneSlot(startX, startY, it, '#e5ae67')
    } else {
      drawOneHole(startX, startY, it, '#e5ae67')
    }
  })
  ctx.value!.globalCompositeOperation = 'source-over'
}
// 点击框出冲突部分
function handleSelctConflictArea() {
  isClickConflict.value = !isClickConflict.value
}
// 开始画冲突
function handleDrawConflictRect() {
  const conflicts = [
    ...(part.value?.holes ?? []),
    ...(part.value?.slots ?? []),
    ...(part.value?.lineSlots ?? []),
  ]
  //思路 获取到冲突区域图像上左最小值下右最大值构成一个矩形就是冲突的矩形）
  let list: {
    left: number[]
    top: number[]
    bottom: number[]
    right: number[]
  }[] = []
  conflicts.forEach((item) => {
    if (item.type === 'hole' && item.conflict) {
      // 获取圆的上下左右
      const {
        center: { x, y },
        diameter,
      } = item as HoleType
      const info = {
        left: [startXBase.value + (x - diameter / 2) * scale.value - gap],
        right: [startXBase.value + (x + diameter / 2) * scale.value + gap],
        bottom: [startYBase.value + (y + diameter / 2) * scale.value + gap],
        top: [startYBase.value + (y - diameter / 2) * scale.value - gap],
      }
      list.push(info)
      // 获取内部和圆有冲突的位置(孔插和孔孔冲突)
      item.conflict?.forEach((it) => {
        const conflictObj = it.conflictObj ?? {}
        const { type, symbol } = conflictObj
        if (type === 'slot' || symbol === 'lineSlot') {
        } else {
          const center = (conflictObj as HoleType).center
          const diameter = Number((conflictObj as HoleType).diameter)
          const x = Number(center?.x)
          const y = Number(center?.y)
          if (
            !Number.isFinite(x) ||
            !Number.isFinite(y) ||
            !Number.isFinite(diameter)
          ) {
            return
          }
          info.left.push(
            startXBase.value + (x - diameter / 2) * scale.value - gap
          )
          info.right.push(
            startXBase.value + (x + diameter / 2) * scale.value + gap
          )
          info.top.push(
            startYBase.value + (y - diameter / 2) * scale.value - gap
          )
          info.bottom.push(
            startYBase.value + (y + diameter / 2) * scale.value + gap
          )
        }
      })
    }
  })
  list = handleSelectBox(list)
  scene.value?.drawDashRect(list)
}

// 检测subset是否是superset的子序列
function check(subset: number[], superset: number[]) {
  return subset.every((item: any) => superset.indexOf(item) !== -1)
}

// 处理最后要绘制选择框的数组
function handleSelectBox(list: any[]) {
  // 过滤相同数据
  const tempList = []
  // 过滤的数组索引
  let superfluousKeys: { [key: string]: any } = []
  list.filter((i) => i.left.sort())
  for (let i = 0; i < list.length; i++) {
    if (list[i].left.length > 2) {
      // 有子集就收集
      tempList.push({ info: list[i].left, index: i })
    }
  }
  // 排除子集
  for (let i = 0; i < list.length; i++) {
    for (let j = 0; j < tempList.length; j++) {
      if (check(list[i].left, tempList[j].info) && i !== tempList[j].index) {
        superfluousKeys.push(i)
      }
    }
  }
  list = list.filter(
    (i: any, index: number) => !superfluousKeys.includes(index)
  )

  return list
}

onMounted(() => {
  nextTick(() => {
    scene.value = new Scene('canvas')
    ctx.value = canvasRef.value!.getContext('2d')
    JSON.parse(JSON.stringify(store.state.finalDrawData)).forEach(
      (item: any) => {
        item.data.forEach((parts: any) => {
          // 过滤掉不包含孔槽冲突的板件并且按照小板切割顺序
          parts.parts = parts.parts
            .filter((i: any) => i.holeSlotMerge)
            .sort((a: any, b: any) => a.priority - b.priority)
          parts.parts.forEach((it: any) => {
            plankNameList.value.push(it.partName)
          })
        })
      }
    )

    // 获取最新的recordSerialNumber值
    emits('searchConflictingPlates', plankNameList.value[props.conflictIndex])
    recordSerialNumber.value = props.conflictIndex
  })
})

watch(
  () => store.state.recordCurrentConflictBoard,
  () => {
    recordSerialNumber.value = props.conflictIndex
    scene.value?.reset()
    part.value = store.state.recordCurrentConflictBoard
    scene.value?.draw(startRender)
    // 保证大板切换正反面选中框不丢失
    isClickConflict.value && handleDrawConflictRect()
    isShow.value = true
  }
)
watch(
  () => props.part,
  (val) => {
    if (!val) return
    scene.value.reset()
    if (isClickConflict.value) {
      handleDrawConflictRect()
    }
    zoomPercent.value = (scene.value.getScaleRate() * 100).toFixed(0)
  }
)
watch(
  () => isClickConflict.value,
  (flag) => {
    scene.value.reset()
    flag && handleDrawConflictRect()
    scene.value?.changeStatus(flag)
    if (!flag) {
      zoomPercent.value = 100
    }
  }
)

return { __sfc: true,fillStyle, props, emits, styleState, canvasState, recordSerialNumber, scale, zoomPercent, clipPath, startX, startY, gap, plankNameList, canvasRef, ctx, part, isShow, isShowCanvas, scene, conflictNum, startXBase, startYBase, isClickConflict, handleCloseConflictDiagram, calcConflictNum, calcScale, updateConflict, handleZoom, startRender, drawShape, drawHoles, drawOneHole, drawSlots, drawObliqueSlot, drawOneSlot, alreadyDraw, drawOneConflictIntersectShape, handleNextBtn, handleWheelScale, handleLastBtn, drawConflictIntersectShape, drawBeyondConflict, handleSelctConflictArea, handleDrawConflictRect, check, handleSelectBox, tips }
}

})